import React, { useMemo, useState, useEffect, memo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Select, Flex, Typography, Space, Grid } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';

import { TrialForm } from '@Features/index';
import { elementsOptions } from '@Features/signin-forms/ui/stripeElements';
import {
    useLazyGetPlansQuery,
    useLazyGetWinbackPlansQuery,
    useLazyGetBlackFridayPlansQuery,
} from '@Entities/signup/api/signupSlice';
import { ga4event } from '@Shared/utils/ga4event';
import { Chevron } from '@Shared/ui';
import './styles.less';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Once per page: Elements must keep the same Stripe instance across renders.
const stripePromise = loadStripe(process.env.STRIPE_API_KEY_PUBLISHABLE || '');

const customTokens = {
    colorSurfaceLightBlueCard: '#F2F7FF',
};

const styles: Record<string, React.CSSProperties> = {
    // The same card as steps 1 and 2 (AuthStep, CompanyStep).
    main: {
        width: 'min(calc(100vw - 40px), 500px)',
        height: 'fit-content',
        // The padding is inside the 500 px, as in step 1's card; outside it this card came out 580 px wide.
        boxSizing: 'border-box',
    },
};

export type PlanType = {
    value?: string;
    cycle?: string;
    title: string;
    trialDays: string;
    price: string;
    id: string;
    currency?: string;
};

// "Launch plan ($89 per month)": the plan's name and its price, written out (Vova 29.10.2026).
const PlanLabel = ({ title, price, cycle, id, currency }: Omit<PlanType, 'trialDays'>) => {
    const breakpoints = useBreakpoint();
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const amount = currency
        ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency,
              minimumFractionDigits: Number(price) % 1 === 0 ? 0 : 2,
          })
              .format(Number(price))
              .replace(',', '')
        : price.replace(',', '');
    const saving = id.includes('propromo2024year')
        ? 40
        : id.includes('enterprisepromo2024year')
          ? 50
          : cycle === 'yearly'
            ? 17
            : null;

    return (
        <h5
            className="xxlSelectOptionTitle"
            style={{ fontSize: breakpoints.md ? 20 : 16 }}
        >
            <span>
                {t(cycle === 'yearly' ? 'trial_step.plan_yearly' : 'trial_step.plan_monthly', { title, price: amount })}
            </span>
            {saving ? <span className="save-tag">{`${t('trial_step.save')} ${saving}%`}</span> : null}
        </h5>
    );
};

// The page hands every step handleStep; this one, the last, never steps back (Vova 29.10.2026: no Back on the payment).
export const TrialStep = memo<{ handleStep: (arg1: number) => void }>(() => {
    const [activePlan, setActivePlan] = useState<PlanType | null>(null);
    // The plan picker's arrow points up while its list is open, as the language picker's does.
    const [planListOpen, setPlanListOpen] = useState(false);
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'signup' });
    const location = useLocation();
    const [fetchPlans, { isLoading: isLoadingPlans, data: dataPlans }] = useLazyGetPlansQuery();
    const [fetchWinbackPlans, { isLoading: isLoadingWinbackPlans, data: dataWinbackPlans }] =
        useLazyGetWinbackPlansQuery();
    const [fetchBlackfridayPlans, { isLoading: isLoadingBlackFridayPlans, data: dataBlackFridayPlans }] =
        useLazyGetBlackFridayPlansQuery();
    const [searchParams] = useSearchParams();
    const breakpoints = useBreakpoint();

    const isWinback = useMemo(() => location.pathname.includes('lovely-user/winback'), [location.pathname]);
    const isBlackfriday = useMemo(() => location.pathname.includes('blackfriday'), [location.pathname]);

    // The regular sign-up offers monthly plans only (Vova 29.10.2026); the win-back and Black Friday pages keep their
    // own lists, where the yearly promo plans are the point.
    const isPromo = isWinback || isBlackfriday;
    const allPlans: PlanType[] = useMemo(
        () => (isWinback ? dataWinbackPlans : isBlackfriday ? dataBlackFridayPlans : dataPlans)?.plans || [],
        [dataPlans, dataWinbackPlans, dataBlackFridayPlans, isBlackfriday, isWinback],
    );
    const planOptions: PlanType[] = useMemo(
        () => (isPromo ? allPlans : allPlans.filter((p) => p.cycle !== 'yearly')),
        [allPlans, isPromo],
    );

    useEffect(() => {
        if (isWinback) {
            fetchWinbackPlans('');
        } else if (isBlackfriday) {
            fetchBlackfridayPlans('');
        } else {
            fetchPlans('');
        }
    }, [isWinback, isBlackfriday, fetchPlans, fetchWinbackPlans, fetchBlackfridayPlans]);

    const handlePlanChange = (newValue: unknown) => {
        const newPlan = planOptions.find(({ id }) => id === newValue)!;
        setActivePlan(newPlan);
        ga4event({
            event_name: 'send_advertiser_payment_method_select_plan',
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;select_plan',
            event_option4: JSON.stringify(newPlan),
        });
    };

    useEffect(() => {
        if (planOptions?.length) {
            // ?pid= from the site's pricing page; a yearly plan there (the page's yearly switch) becomes the same plan,
            // monthly. Without it, Launch monthly.
            const pid = searchParams.get('pid');
            const asked = pid ? allPlans.find((p) => p.id.startsWith(pid)) : undefined;
            const plan =
                planOptions.find((p) => p.id === asked?.id) ??
                planOptions.find((p) => p.title === asked?.title) ??
                planOptions.find((p) => p.title === 'Launch') ??
                planOptions[0];
            setActivePlan(plan);
        }
        ga4event({
            event_name: 'send_advertiser_payment_method_select_plan',
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;select_plan',
            event_option4: JSON.stringify(planOptions[0]),
        });
    }, [planOptions, allPlans, searchParams]);

    const planOptionsWithLabel = useMemo(
        () =>
            planOptions.map(({ title, price, cycle, id, currency }) => ({
                value: id,
                label: (
                    <PlanLabel
                        title={title}
                        price={price}
                        cycle={cycle}
                        id={id}
                        currency={currency}
                    />
                ),
            })),
        [planOptions],
    );

    const isLoading = isLoadingPlans || isLoadingWinbackPlans || isLoadingBlackFridayPlans;

    // One column, the same card as steps 1 and 2 (Vova 29.10.2026): the testimonial that stood beside the form on wide
    // screens is gone.
    return (
        <div style={{ ...styles.main, padding: breakpoints.lg ? 40 : 20 }}>
            <Title
                level={4}
                style={{ marginTop: 0, marginBottom: 24, textAlign: 'center' }}
            >
                {isBlackfriday
                    ? t('trial_step.title_blackfriday', { trialDays: activePlan?.trialDays || '14' })
                    : t('trial_step.title', { trialDays: activePlan?.trialDays || '14' })}
            </Title>

            {/* One light-blue block: the plan, big and blue, on top; the promise in one bold line under it
                (Vova 29.10.2026). */}
            <Space
                direction="vertical"
                style={{
                    width: '100%',
                    background: customTokens.colorSurfaceLightBlueCard,
                    borderRadius: 'var(--ant-border-radius-lg)',
                    // More room under the plan than between fields: the wallet buttons belong to the payment below.
                    marginBottom: 32,
                }}
            >
                <Select
                    value={activePlan?.id || ''}
                    size="large"
                    className="xxlSelect"
                    options={planOptionsWithLabel}
                    onChange={handlePlanChange}
                    onDropdownVisibleChange={setPlanListOpen}
                    suffixIcon={
                        <Chevron
                            size={22}
                            up={planListOpen}
                            color="#8c8c8c"
                        />
                    }
                    loading={isLoading}
                    listHeight={400}
                />
                <Flex
                    gap="middle"
                    align="center"
                    style={{ padding: '4px 16px 12px' }}
                >
                    <SafetyCertificateOutlined style={{ fontSize: 24, color: 'var(--ant-color-primary)' }} />
                    <Text
                        strong
                        style={{ fontSize: 13 }}
                    >
                        {t('trial_step.plan_warning', { trialDays: activePlan?.trialDays || '14' })}
                    </Text>
                </Flex>
            </Space>
            {/* Stripe's group for the card form (Payment Element), in Stripe's look and the page's language; Apple
                Pay / Google Pay are buttons of their own group in TrialForm. Deferred mode: the card becomes a payment
                method here, as before, with no intent, so the server takes it unchanged. */}
            <Elements
                // Stripe's form takes its language once: a new language mounts it anew.
                key={i18n.language}
                stripe={stripePromise}
                options={elementsOptions(activePlan?.currency, i18n.language)}
            >
                <TrialForm
                    activePlan={activePlan!}
                    isPlansLoading={isLoading}
                />
            </Elements>
        </div>
    );
});

TrialStep.displayName = 'TrialStep';
